import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Button, Checkbox, Input, Select } from '@artosera/ui';
import { CATEGORIES_CONVERSATION, formatDate } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import {
  boiteApi,
  useConversation,
  useActionBoite,
  useReglagesBoite,
  type Conversation,
  type Message,
  type Categorie,
} from '../../api/boite-reception.js';
import { useContact } from '../../api/contacts.js';
import { useEquipe } from '../../api/equipe.js';
import { useOeuvre, useOeuvres } from '../../api/oeuvres.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { NotFoundPage } from '../introuvable/NotFoundPage.js';
import { estApiError } from '../../api/errors.js';
import { ChoixContact } from '../pipeline/ChoixContact.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { useGesteBoite } from './useGesteBoite.js';
import { MessageEntrant, PiecesJointes } from './MessageEntrant.js';
import { Reponse } from './Reponse.js';
import { AssistantConversation } from './AssistantConversation.js';
import styles from './Boite.module.css';
export function ConversationVue({ id }: { id: string }) {
  const { galerieSlug } = useShellContext();
  const { t } = useTranslation(['regie', 'commun']);
  const fiche = useConversation(galerieSlug, id);
  if (fiche.isLoading) return <p role="status">{t('commun:chargement')}</p>;
  if (estApiError(fiche.error) && fiche.error.status === 404) return <NotFoundPage />;
  if (!fiche.data) return <p role="alert">{t('commun:erreurReseau')}</p>;
  return <ConversationContenu c={fiche.data.conversation} messages={fiche.data.messages} />;
}
function ConversationContenu({ c, messages }: { c: Conversation; messages: Message[] }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const ecrire = galerieALaction(galerie, 'inbox:write');

  const assistantActif = galerieAModule(galerie, 'assistant-reponse');

  const [prepare, setPrepare] = useState<{ message: Message; messages: Message[] } | null>(null);

  const dejaLue = useRef(false);
  const lecture = useActionBoite(galerieSlug, (_: void, appel) =>
    boiteApi.modifier(galerieSlug, c.id, { version: c.version, nonLue: false }, appel),
  );
  const marquerLue = lecture.mutate;
  useEffect(() => {
    if (c.nonLue && ecrire && !dejaLue.current) {
      dejaLue.current = true;
      marquerLue();
    }
  }, [c.nonLue, ecrire, marquerLue]);
  const contact = useContact(
    galerieSlug,
    galerieALaction(galerie, 'contacts:read') ? c.contactId : null,
  );
  const equipe = useEquipe(galerieSlug, {
    enabled: galerieALaction(galerie, 'memberships:read'),
  });
  const reglages = useReglagesBoite(galerieSlug);
  const [rattachement, setRattachement] = useState<{ id: string; nom: string } | null>(null);
  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche);
  const oeuvres = useOeuvres(
    galerieSlug,
    { q: rechercheDebattue, parPage: 20 },
    {
      enabled: ecrire && galerieALaction(galerie, 'artworks:read') && rechercheDebattue.length >= 2,
    },
  );
  const [attente, setAttente] = useState('');
  const modifier = useGesteBoite((corps: object, appel) =>
    boiteApi.modifier(galerieSlug, c.id, { ...corps, version: c.version }, appel),
  );
  const geste = useGesteBoite((entree: { chemin: string; corps?: object }, appel) =>
    boiteApi.geste(galerieSlug, entree.chemin, { ...entree.corps, version: c.version }, appel),
  );
  function agir(action: string, corps?: object) {
    void geste.executer({ chemin: `/conversations/${c.id}/${action}`, corps });
  }
  function differer(type: 'demain' | 'lundi' | 'semaine') {
    const date = new Date();
    if (type === 'demain') date.setDate(date.getDate() + 1);
    if (type === 'lundi') date.setDate(date.getDate() + ((8 - date.getDay()) % 7 || 7));
    if (type === 'semaine') date.setDate(date.getDate() + 7);
    date.setHours(9, 0, 0, 0);
    agir('attente', { jusquau: date.toISOString() });
  }
  const desactive = modifier.pending || geste.pending || lecture.isPending;
  const brouillon =
    messages.find((m) => m.sens === 'sortant' && m.etat === 'brouillon') ??
    (prepare?.messages === messages ? prepare.message : undefined);
  return (
    <article className={styles.conversation}>
      <h2>{c.sujet || t('regie:boiteReception.sansSujet')}</h2>
      {c.contactId && galerieALaction(galerie, 'contacts:read') ? (
        <Link to={`/${galerieSlug}/contacts/${c.contactId}`}>
          {contact.data?.contact.nomAffiche ?? (c.correspondant.nom || c.correspondant.adresse)}
        </Link>
      ) : (
        <p>
          {c.correspondant.nom} {c.correspondant.adresse}
        </p>
      )}
      <p>
        {t(`regie:boiteReception.canaux.${c.canal}`)} ·{' '}
        {c.categorie
          ? t(`regie:boiteReception.categories.${c.categorie}`)
          : t('regie:boiteReception.sansCategorie')}{' '}
        · {c.etiquettes.join(' · ')} ·{' '}
        {equipe.data?.membres.find((m) => m.compteId === c.assigneeA)?.nom ??
          t(c.assigneeA ? 'regie:boiteReception.assignee' : 'regie:boiteReception.sansAssignation')}
      </p>
      {assistantActif && (
        <AssistantConversation
          c={c}
          occupe={desactive}
          preparer={(message) => setPrepare({ message, messages })}
        />
      )}
      {ecrire && !c.contactEfface ? (
        <fieldset disabled={desactive} className={styles.formulaire}>
          <legend>{t('regie:boiteReception.gestion')}</legend>
          {!c.contactId && galerieALaction(galerie, 'contacts:write') ? (
            <>
              <Button variant="secondary" onClick={() => agir('contact')}>
                {t('regie:boiteReception.creerContact')}
              </Button>
              {galerieALaction(galerie, 'contacts:read') ? (
                <>
                  <ChoixContact valeur={rattachement} onChange={setRattachement} />
                  <Button
                    variant="secondary"
                    disabled={!rattachement}
                    onClick={() => agir('contact', { contactId: rattachement!.id })}
                  >
                    {t('regie:boiteReception.rattacherContact')}
                  </Button>
                </>
              ) : null}
            </>
          ) : null}
          <Field htmlFor="conversation-categorie" label={t('regie:boiteReception.categorie')}>
            <Select
              id="conversation-categorie"
              value={c.categorie ?? ''}
              disabled={desactive}
              onValueChange={(v) =>
                void modifier.executer({ categorie: (v || null) as Categorie | null })
              }
              options={[
                { value: '', label: t('regie:boiteReception.sansCategorie') },
                ...CATEGORIES_CONVERSATION.map((value) => ({
                  value,
                  label: t(`regie:boiteReception.categories.${value}`),
                })),
              ]}
            />
          </Field>
          <div>
            {reglages.data?.reglages.etiquettes.map((etiquette, i) => (
              <Checkbox
                key={etiquette}
                id={`conversation-etiquette-${i}`}
                label={etiquette}
                disabled={desactive}
                checked={c.etiquettes.includes(etiquette)}
                onCheckedChange={(v) =>
                  void modifier.executer({
                    etiquettes: v
                      ? [...c.etiquettes, etiquette]
                      : c.etiquettes.filter((e) => e !== etiquette),
                  })
                }
              />
            ))}
          </div>
          {galerieALaction(galerie, 'memberships:read') ? (
            <Field htmlFor="conversation-assignation" label={t('regie:boiteReception.assignation')}>
              <Select
                id="conversation-assignation"
                value={c.assigneeA ?? ''}
                disabled={desactive}
                onValueChange={(v) => void modifier.executer({ assigneeA: v || null })}
                options={[
                  { value: '', label: t('regie:boiteReception.sansAssignation') },
                  ...(equipe.data?.membres.map((m) => ({ value: m.compteId, label: m.nom })) ?? []),
                ]}
              />
            </Field>
          ) : null}
          {galerieALaction(galerie, 'artworks:read') ? (
            <>
              <Field htmlFor="conversation-oeuvre" label={t('regie:boiteReception.ajouterOeuvre')}>
                <Input
                  id="conversation-oeuvre"
                  type="search"
                  value={recherche}
                  onChange={(e) => setRecherche(e.target.value)}
                />
              </Field>
              {oeuvres.data?.oeuvres
                .filter((o) => !c.oeuvreIds.includes(o.id))
                .map((o) => (
                  <Button
                    key={o.id}
                    variant="ghost"
                    disabled={c.oeuvreIds.length >= 20}
                    onClick={() => {
                      void modifier.executer({ oeuvreIds: [...c.oeuvreIds, o.id] });
                      setRecherche('');
                    }}
                  >
                    {o.titre} · {o.artisteNom}
                  </Button>
                ))}
            </>
          ) : null}
          <div className={styles.gestes}>
            <Button variant="secondary" onClick={() => differer('demain')}>
              {t('regie:boiteReception.demain')}
            </Button>
            <Button variant="secondary" onClick={() => differer('lundi')}>
              {t('regie:boiteReception.lundi')}
            </Button>
            <Button variant="secondary" onClick={() => differer('semaine')}>
              {t('regie:boiteReception.semaine')}
            </Button>
          </div>
          <form
            className={styles.gestes}
            onSubmit={(e) => {
              e.preventDefault();
              agir('attente', { jusquau: new Date(attente).toISOString() });
            }}
          >
            <Field htmlFor="conversation-attente" label={t('regie:boiteReception.dateAttente')}>
              <Input
                id="conversation-attente"
                type="datetime-local"
                required
                value={attente}
                onChange={(e) => setAttente(e.target.value)}
              />
            </Field>
            <Button type="submit" variant="secondary">
              {t('regie:boiteReception.mettreAttente')}
            </Button>
            {c.enAttenteJusquau ? (
              <Button
                type="button"
                variant="secondary"
                onClick={() => agir('attente', { jusquau: null })}
              >
                {t('regie:boiteReception.leverAttente')}
              </Button>
            ) : null}
          </form>
          <div className={styles.gestes}>
            <Button
              variant="secondary"
              onClick={() => agir('etat', { etat: c.etat === 'traitee' ? 'ouverte' : 'traitee' })}
            >
              {t(
                c.etat === 'traitee'
                  ? 'regie:boiteReception.rouvrir'
                  : 'regie:boiteReception.traiter',
              )}
            </Button>
            <Button variant="secondary" onClick={() => agir('etat', { etat: 'indesirable' })}>
              {t('regie:boiteReception.indesirable')}
            </Button>
            {c.automatique || c.etat === 'indesirable' ? (
              <Button variant="secondary" onClick={() => agir('etat', { etat: 'principale' })}>
                {t('regie:boiteReception.principale')}
              </Button>
            ) : null}
            <Button
              variant="secondary"
              onClick={() =>
                void geste.executer({
                  chemin: '/expediteurs-ignores',
                  corps: { conversationId: c.id },
                })
              }
            >
              {t('regie:boiteReception.ignorer')}
            </Button>
            <Button variant="secondary" onClick={() => agir('repondu-hors-boite')}>
              {t('regie:boiteReception.reponduHorsBoite')}
            </Button>
          </div>
        </fieldset>
      ) : null}
      <ul>
        {c.oeuvreIds.map((id) => (
          <li key={id}>
            <OeuvreLiee id={id} />
            {ecrire ? (
              <Button
                variant="ghost"
                disabled={desactive}
                onClick={() =>
                  void modifier.executer({ oeuvreIds: c.oeuvreIds.filter((o) => o !== id) })
                }
                aria-label={t('regie:boiteReception.retirerOeuvre', { id })}
              >
                {t('regie:boiteReception.retirer')}
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
      {c.enAttenteJusquau ? (
        <p>
          {t('regie:boiteReception.attenteJusquau', {
            date: formatDate(new Date(c.enAttenteJusquau), i18n.language === 'en' ? 'en' : 'fr'),
          })}
        </p>
      ) : null}
      {modifier.erreur || geste.erreur || lecture.isError ? (
        <p role="alert">
          {modifier.erreur ||
            geste.erreur ||
            (estApiError(lecture.error) ? lecture.error.message : t('commun:erreurReseau'))}
        </p>
      ) : null}
      <div className={styles.conversation}>
        {messages.map((m) => (
          <article
            key={m.id}
            className={`${styles.message} ${m.sens === 'note' ? styles.note : ''}`}
          >
            <h3>
              {t(`regie:boiteReception.sens.${m.sens}`)}{' '}
              {m.sens === 'note'
                ? `· ${t('regie:boiteReception.interne')}`
                : m.de.nom || m.de.adresse}
            </h3>
            <p>
              <time dateTime={m.creeLe}>
                {formatDate(new Date(m.creeLe), i18n.language === 'en' ? 'en' : 'fr')}
              </time>{' '}
              · {t(`regie:boiteReception.etatsMessage.${m.etat}`)}
            </p>
            {assistantActif &&
              m.auteurType === 'SYSTEME' &&
              m.assistant?.reponsePratique &&
              m.etat === 'envoye' && <p>{t('regie:assistantReponse.automatique')}</p>}
            {m.sens === 'entrant' ? (
              <MessageEntrant message={m} />
            ) : (
              <>
                <p className={styles.texte}>{m.texte}</p>
                <PiecesJointes message={m} />
              </>
            )}
            {m.suivi ? (
              <p>
                {t('regie:boiteReception.ouvertures', { count: m.suivi.ouvertures })} ·{' '}
                {t('regie:boiteReception.indicatif')}
                {m.suivi.premiereOuvertureLe
                  ? ` · ${formatDate(new Date(m.suivi.premiereOuvertureLe), i18n.language === 'en' ? 'en' : 'fr')}`
                  : ''}
              </p>
            ) : null}
            {m.etat === 'echec' ? (
              <>
                <p>{m.erreurEnvoi}</p>
                {ecrire ? (
                  <Button
                    disabled={desactive}
                    onClick={() => void geste.executer({ chemin: `/messages/${m.id}/reessayer` })}
                  >
                    {t('regie:boiteReception.reessayer')}
                  </Button>
                ) : null}
              </>
            ) : null}
          </article>
        ))}
      </div>
      {ecrire && !c.contactEfface ? (
        <>
          {assistantActif && (
            <p role="status">
              {prepare && prepare.message.id === brouillon?.id
                ? t('regie:assistantReponse.brouillonPrepare')
                : ''}
            </p>
          )}
          <Reponse
            key={brouillon?.id ?? c.id}
            conversation={c}
            brouillon={brouillon}
            presentationBrouillon={assistantActif ? prepare?.message.id : undefined}
          />
        </>
      ) : null}
    </article>
  );
}
function OeuvreLiee({ id }: { id: string }) {
  const { galerieSlug, galerie } = useShellContext();
  const { t } = useTranslation('regie');
  const oeuvre = useOeuvre(galerieSlug, galerieALaction(galerie, 'artworks:read') ? id : null);
  if (!galerieALaction(galerie, 'artworks:read'))
    return <span>{t('boiteReception.oeuvreLiee')}</span>;
  return (
    <Link to={`/${galerieSlug}/oeuvres/${id}`}>
      {oeuvre.data?.oeuvre.titre ?? t('boiteReception.oeuvreLiee')}
    </Link>
  );
}
